import type { PublicEvent, BroadcastColorMode, BroadcastBlinkInterval, BroadcastHoldDuration } from '@arenapulse/shared';

export type ApiResponse<T> =
  | { success: true; data: T }
  | { success: false; error: { code: string; message: string } };

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Helper to get a cookie value by name
export function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  if (match) return match[2];
  return null;
}

async function fetchApi<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // If request is POST/PUT/PATCH/DELETE, attach CSRF token
    const method = options.method?.toUpperCase() || 'GET';
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      const csrfToken = getCookie('csrf_token');
      if (csrfToken) {
        (headers as Record<string, string>)['X-CSRF-Token'] = csrfToken;
      }
    }

    const response = await fetch(`${BASE_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include', // Crucial for sending/receiving HttpOnly cookies
    });

    const data = await response.json();
    return data as ApiResponse<T>;
  } catch (error) {
    if (error instanceof TypeError) {
      // Network error / Offline
      return {
        success: false,
        error: { code: 'OFFLINE', message: 'Network connection failed' },
      };
    }
    return {
      success: false,
      error: { code: 'INTERNAL_ERROR', message: 'An unexpected error occurred' },
    };
  }
}

export const ApiClient = {
  checkSession: () => {
    return fetchApi<unknown>('/api/v1/auth/session', {
      method: 'GET',
    });
  },

  getEventPresence: (id: string) => {
    return fetchApi<{ count: number }>(`/api/v1/events/${id}/presence`, {
      method: 'GET',
    });
  },

  getEvents: () => {
    return fetchApi<PublicEvent[]>('/api/v1/events', {
      method: 'GET',
    });
  },

  createEvent: (data: { name: string; description?: string }) => {
    return fetchApi<PublicEvent>('/api/v1/events', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  deleteEvent: (id: string) => {
    return fetchApi<{ id: string; deleted: boolean }>(`/api/v1/events/${id}`, {
      method: 'DELETE',
    });
  },

  startEvent: (id: string) => {
    return fetchApi<PublicEvent>(`/api/v1/events/${id}/start`, {
      method: 'POST',
    });
  },

  pauseEvent: (id: string) => {
    return fetchApi<PublicEvent>(`/api/v1/events/${id}/pause`, {
      method: 'POST',
    });
  },

  resumeEvent: (id: string) => {
    return fetchApi<PublicEvent>(`/api/v1/events/${id}/resume`, {
      method: 'POST',
    });
  },

  endEvent: (id: string) => {
    return fetchApi<PublicEvent>(`/api/v1/events/${id}/end`, {
      method: 'POST',
    });
  },

  broadcastColor: (id: string, color: string, mode: BroadcastColorMode, blinkIntervalMs: BroadcastBlinkInterval, holdDuration: BroadcastHoldDuration) => {
    return fetchApi<unknown>(`/api/v1/events/${id}/broadcast/color`, {
      method: 'POST',
      body: JSON.stringify({ color, mode, blinkIntervalMs, holdDuration }),
    });
  },

  broadcastEmoji: (id: string, emoji: string) => {
    return fetchApi<unknown>(`/api/v1/events/${id}/broadcast/emoji`, {
      method: 'POST',
      body: JSON.stringify({ emoji }),
    });
  },

  broadcastCountdown: (id: string, seconds: number) => {
    return fetchApi<unknown>(`/api/v1/events/${id}/broadcast/countdown`, {
      method: 'POST',
      body: JSON.stringify({ seconds }),
    });
  },

  logout: () => {
    return fetchApi<unknown>('/api/v1/auth/logout', {
      method: 'POST',
    });
  },
};
